import React, { useState } from 'react';
import { Camera, Lock, Globe, User, Calendar, Image, Trash2, Edit2 } from 'lucide-react';
import './AlbumGrid.css';
import { getApiUrl } from '../utils/config';
import authService from '../services/authService';

const AlbumGrid = ({ albums, viewMode, onSelectAlbum, onDeleteAlbum, onEditAlbum, currentUser, emptyMessage }) => {
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const getAlbumThumbnail = (album) => {
    if (album.cover_photo_id || album.cover_thumbnail) {
      return `${getApiUrl()}/api/albums/${album.id}/thumbnail`;
    }
    return null;
  };

  const handleDeleteClick = (e, album) => {
    e.stopPropagation();
    setDeleteConfirm(album);
  };

  const handleConfirmDelete = async (e) => {
    e.stopPropagation();
    if (!deleteConfirm) return;

    setDeleting(true);
    try {
      const response = await authService.authenticatedFetch(`${getApiUrl()}/api/albums/${deleteConfirm.id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        const result = await response.json();
        console.log(result.message);
        
        // Call parent's onDeleteAlbum callback if provided
        if (onDeleteAlbum) {
          onDeleteAlbum(deleteConfirm.id);
        }
        
        setDeleteConfirm(null);
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to delete album');
      }
    } catch (error) {
      console.error('Error deleting album:', error);
      alert('Error deleting album');
    } finally {
      setDeleting(false);
    }
  };

  const handleCancelDelete = (e) => {
    e.stopPropagation();
    setDeleteConfirm(null);
  };

  const canDeleteAlbum = (album) => {
    return currentUser && album.user_id === currentUser.id;
  };

  if (albums.length === 0) {
    return (
      <div className="empty-state">
        <Camera size={64} className="empty-icon" />
        <p>{emptyMessage}</p>
      </div>
    );
  }

  return (
    <>
      <div className={viewMode === 'grid' ? 'album-grid' : 'album-list'}>
        {albums.map(album => (
          <div 
            key={album.id} 
            className={viewMode === 'grid' ? 'album-card' : 'album-list-item'}
            onClick={() => onSelectAlbum(album)}
          >
            <div className={viewMode === 'grid' ? 'album-thumbnail' : 'album-list-thumbnail'}>
              {getAlbumThumbnail(album) ? (
                <img 
                  src={getAlbumThumbnail(album)} 
                  alt={album.name}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
              ) : null}
              
              <div 
                className="album-placeholder" 
                style={{display: getAlbumThumbnail(album) ? 'none' : 'flex'}}
              >
                <Camera size={48} />
                <p>No cover photo</p>
              </div>
              
              {viewMode === 'grid' && (
                <>
                  <div className="album-overlay">
                    <span className="photo-count">
                      <Image size={16} />
                      {album.photo_count || 0} photos
                    </span>
                  </div>
                  
                  <div className={`album-badge ${album.is_public ? 'public' : 'private'}`}>
                    {album.is_public ? <Globe size={14} /> : <Lock size={14} />}
                  </div>
                </>
              )}

              {canDeleteAlbum(album) && (
                <div className="album-actions-overlay">
                  {onEditAlbum && (
                    <button 
                      className="album-action edit-action"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEditAlbum(album);
                      }}
                      title="Edit album"
                    >
                      <Edit2 size={16} />
                    </button>
                  )}
                  <button 
                    className="album-action delete-action"
                    onClick={(e) => handleDeleteClick(e, album)}
                    title="Delete album"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              )}
            </div>
            
            <div className={viewMode === 'grid' ? 'album-info' : 'album-list-info'}>
              <h3>{album.name}</h3>
              {viewMode === 'list' && album.description && (
                <p className="album-description">{album.description}</p>
              )}
              {album.username && (
                <p className="album-author">
                  <User size={14} />
                  {album.username}
                </p>
              )}
              {viewMode === 'list' && (
                <div className="album-meta">
                  <span>
                    <Image size={14} />
                    {album.photo_count || 0} photos
                  </span>
                  <span>
                    <Calendar size={14} />
                    {formatDate(album.updated_at)}
                  </span>
                  <span className={`privacy-badge ${album.is_public ? 'public' : 'private'}`}>
                    {album.is_public ? <Globe size={14} /> : <Lock size={14} />}
                    {album.is_public ? 'Public' : 'Private'}
                  </span>
                </div>
              )}
              {viewMode === 'grid' && (
                <p className="album-date">{formatDate(album.updated_at)}</p>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="delete-confirm-overlay" onClick={handleCancelDelete}>
          <div className="delete-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Delete Album</h3>
            <p>Are you sure you want to delete "{deleteConfirm.name}"?</p>
            <p className="delete-warning">
              This will permanently delete the album and all {deleteConfirm.photo_count || 0} photos in it. This action cannot be undone.
            </p>
            <div className="delete-confirm-actions">
              <button 
                className="btn-cancel" 
                onClick={handleCancelDelete}
                disabled={deleting}
              >
                Cancel
              </button>
              <button 
                className="btn-delete" 
                onClick={handleConfirmDelete}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete Album'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AlbumGrid;